import React, { useEffect, useState, useCallback } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import Stack from "@mui/joy/Stack";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Chip from "@mui/joy/Chip";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import Tooltip from "@mui/joy/Tooltip";
import CircularProgress from "@mui/joy/CircularProgress";
import Alert from "@mui/joy/Alert";
import Divider from "@mui/joy/Divider";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import LinearProgress from "@mui/joy/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SyncIcon from "@mui/icons-material/Sync";
import PauseIcon from "@mui/icons-material/Pause";
import {
  useBKGIngestionQueue,
  useBKGIngestionSchedule,
  useBKGTriggerIndexing,
  useBKGIndexingProgress,
  IngestionQueueResponse,
  IngestionScheduleResponse,
  IndexingProgressResponse,
} from "../../../services/api";
import { HeaderCell } from "../../HeaderCell";

interface ViewQueueModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

const formatRelativeTime = (dateString: string | null): string => {
  if (!dateString) return "Never";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);

  if (diffMs < 0) {
    // Past
    const absMins = Math.abs(diffMins);
    const absHours = Math.abs(diffHours);
    if (absMins < 60) return `${absMins} min ago`;
    if (absHours < 24) return `${absHours}h ago`;
    return date.toLocaleDateString();
  } else {
    // Future
    if (diffMins < 60) return `in ${diffMins} min`;
    if (diffHours < 24) return `in ${diffHours}h`;
    return date.toLocaleDateString();
  }
};

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString();
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<
    string,
    {
      color: "success" | "danger" | "warning" | "neutral";
      icon: React.ReactNode;
    }
  > = {
    completed: {
      color: "success",
      icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
    },
    error: { color: "danger", icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
    pending: {
      color: "warning",
      icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
    },
    ignored: { color: "neutral", icon: null },
    success: {
      color: "success",
      icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
    },
    skipped: { color: "neutral", icon: <SkipNextIcon sx={{ fontSize: 14 }} /> },
    running: {
      color: "primary" as any,
      icon: <SyncIcon sx={{ fontSize: 14 }} />,
    },
    idle: { color: "neutral", icon: <PauseIcon sx={{ fontSize: 14 }} /> },
  };
  const { color, icon } = config[status] || config.pending;

  return (
    <Chip size="sm" color={color} startDecorator={icon}>
      {status}
    </Chip>
  );
};

const BkgIndexingExplainer: React.FC = () => {
  const stages = [
    "Seed item",
    "Ingestion queue",
    "Indexer run",
    "Field enrichment",
    "Search views",
  ];

  return (
    <Stack spacing={1}>
      <Typography level="title-sm">How new BKG data becomes searchable</Typography>
      <Typography level="body-sm" color="neutral">
        Add Company and Add Conference create small seed items first. The
        indexer runs on schedule or when you click Index Now, enriches missing
        fields, then rebuilds search views so results update.
      </Typography>
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
        {stages.map((stage, index) => (
          <Chip key={stage} size="sm" variant="outlined" color="neutral">
            {index + 1}. {stage}
          </Chip>
        ))}
      </Stack>
      <Typography level="body-xs" color="neutral">
        Pending means waiting for the indexer. Completed means the seed was
        accepted. Ignored usually means duplicate or no new data. Error means
        the item could not be processed.
      </Typography>
    </Stack>
  );
};

const ViewQueueModal: React.FC<ViewQueueModalProps> = ({
  open,
  onClose,
  workspaceId,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [showExplainer, setShowExplainer] = useState(false);
  const [queueData, setQueueData] = useState<IngestionQueueResponse | null>(
    null,
  );
  const [scheduleData, setScheduleData] =
    useState<IngestionScheduleResponse | null>(null);
  const [progressData, setProgressData] =
    useState<IndexingProgressResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triggerMessage, setTriggerMessage] = useState<string | null>(null);

  const fetchQueue = useBKGIngestionQueue();
  const fetchSchedule = useBKGIngestionSchedule();
  const fetchProgress = useBKGIndexingProgress();
  const triggerIndexing = useBKGTriggerIndexing();

  const loadData = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const [queue, schedule, progress] = await Promise.all([
        fetchQueue(workspaceId, { limit: 20, showProcessed: true }),
        fetchSchedule(workspaceId),
        fetchProgress(workspaceId),
      ]);
      setQueueData(queue);
      setScheduleData(schedule);
      setProgressData(progress);
    } catch (err: any) {
      setError(err.message || "Failed to load queue data");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, fetchQueue, fetchSchedule, fetchProgress]);

  // Initial load and auto-refresh
  useEffect(() => {
    if (!open) {
      setQueueData(null);
      setScheduleData(null);
      setProgressData(null);
      setError(null);
      setTriggerMessage(null);
      return;
    }

    loadData();

    // Auto-refresh every 5 seconds (faster for real-time progress)
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [open, loadData]);

  const handleTrigger = async () => {
    setTriggering(true);
    setTriggerMessage(null);
    try {
      const result = await triggerIndexing(workspaceId);
      setTriggerMessage(result.message || "Indexing triggered successfully");
      // Switch to indexing tab to show progress
      setActiveTab(1);
      // Refresh data after triggering
      setTimeout(loadData, 1000);
    } catch (err: any) {
      setError(err.message || "Failed to trigger indexing");
    } finally {
      setTriggering(false);
    }
  };

  const progressPercentage = progressData?.stats
    ? progressData.stats.total_entities > 0
      ? Math.round(
          (progressData.stats.completed_entities /
            progressData.stats.total_entities) *
            100,
        )
      : 0
    : 0;

  return (
    <>
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        className="modal"
        sx={{ width: 750 }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <span>Queue &amp; Indexing Progress</span>
              <Tooltip title="How indexing works" placement="right">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => setShowExplainer(true)}
                >
                  <HelpOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              startDecorator={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </DialogTitle>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {triggerMessage && (
          <Alert color="success" sx={{ mb: 2 }}>
            {triggerMessage}
          </Alert>
        )}

        {loading && !queueData ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value as number)}
              sx={{ borderRadius: "sm" }}
            >
              <TabList disableUnderline>
                <Tab>
                  Ingestion Queue
                  {queueData && queueData.pending > 0 && (
                    <Chip size="sm" color="warning" sx={{ ml: 1 }}>
                      {queueData.pending}
                    </Chip>
                  )}
                </Tab>
                <Tab>
                  Field Indexing
                  {progressData?.status === "running" && (
                    <Chip size="sm" color="primary" sx={{ ml: 1 }}>
                      <SyncIcon
                        sx={{
                          fontSize: 12,
                          mr: 0.5,
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      Active
                    </Chip>
                  )}
                </Tab>
              </TabList>

              {/* Ingestion Queue Tab */}
              <TabPanel
                value={0}
                sx={{ pb: 1 }}
              >
                <Stack spacing={2}>
                  {/* Stats Row */}
                  {queueData && (
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Chip color="warning" variant="soft" size="lg">
                        Pending: {queueData.pending}
                      </Chip>
                      <Chip color="success" variant="soft" size="lg">
                        Completed: {queueData.completed}
                      </Chip>
                      <Chip color="danger" variant="soft" size="lg">
                        Errors: {queueData.error}
                      </Chip>
                      <Chip color="neutral" variant="soft" size="lg">
                        Ignored: {queueData.ignored}
                      </Chip>
                    </Stack>
                  )}

                  <Divider />

                  {/* Schedule Info */}
                  {scheduleData && (
                    <Box>
                      <Typography level="title-sm" sx={{ mb: 1 }}>
                        Indexing Schedule
                      </Typography>
                      <Stack spacing={0.5}>
                        <Typography level="body-sm" component="div">
                          <strong>Status:</strong>{" "}
                          {scheduleData.pending_items > 0 ? (
                            <>
                              <Chip size="sm" color="warning">
                                {scheduleData.pending_items} item
                                {scheduleData.pending_items > 1 ? "s" : ""}{" "}
                                awaiting processing
                              </Chip>{" "}
                              <Typography
                                level="body-xs"
                                component="span"
                                color="neutral"
                              >
                                (click "Index Now" to process)
                              </Typography>
                            </>
                          ) : (
                            <Chip size="sm" color="success">
                              Queue empty
                            </Chip>
                          )}
                        </Typography>
                        <Typography level="body-sm">
                          <strong>Next scheduled run:</strong>{" "}
                          {scheduleData.next_scheduled_run
                            ? formatRelativeTime(
                                scheduleData.next_scheduled_run,
                              )
                            : "Unknown"}
                        </Typography>
                        {scheduleData.last_indexing_completed && (
                          <Typography level="body-sm">
                            <strong>Last indexer run:</strong>{" "}
                            {formatDateTime(
                              scheduleData.last_indexing_completed,
                            )}
                          </Typography>
                        )}
                        <Typography level="body-xs" color="neutral">
                          Scheduled indexer runs every{" "}
                          {scheduleData.scheduled_interval_hours}h
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                  <Divider />

                  {/* Queue Items Table */}
                  <Box>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      Recent Queue Items
                    </Typography>
                    {queueData && queueData.items.length > 0 ? (
                      <Sheet
                        variant="outlined"
                        sx={{
                          borderRadius: "sm",
                          overflow: "auto",
                          maxHeight: 200,
                        }}
                      >
                        <Table size="sm" stickyHeader>
                          <thead>
                            <tr>
                              <HeaderCell style={{ width: "35%" }}>
                                Name
                              </HeaderCell>
                              <HeaderCell style={{ width: "15%" }}>
                                Status
                              </HeaderCell>
                              <HeaderCell style={{ width: "20%" }}>
                                Index
                              </HeaderCell>
                              <HeaderCell style={{ width: "30%" }}>
                                Added
                              </HeaderCell>
                            </tr>
                          </thead>
                          <tbody>
                            {queueData.items.map((item) => (
                              <tr key={item._id}>
                                <td>
                                  <Typography level="body-sm" noWrap>
                                    {item.name || "—"}
                                  </Typography>
                                </td>
                                <td>
                                  <StatusChip status={item.status} />
                                </td>
                                <td>
                                  <Typography level="body-xs" color="neutral">
                                    {item.index_name || "—"}
                                  </Typography>
                                </td>
                                <td>
                                  <Typography level="body-xs" color="neutral">
                                    {formatDateTime(item.ingestion_date)}
                                  </Typography>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Sheet>
                    ) : (
                      <Typography level="body-sm" color="neutral">
                        No items in queue
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </TabPanel>

              {/* Field Indexing Tab */}
              <TabPanel
                value={1}
                sx={{ pb: 1 }}
              >
                <Stack spacing={2}>
                  {/* Status and Progress */}
                  {progressData && (
                    <>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <StatusChip status={progressData.status} />
                        {progressData.index_name && (
                          <Typography level="body-sm" color="neutral">
                            Indexing: {progressData.index_name}
                          </Typography>
                        )}
                        {progressData.started_at && (
                          <Typography level="body-xs" color="neutral">
                            Started: {formatDateTime(progressData.started_at)}
                          </Typography>
                        )}
                      </Stack>

                      {/* Progress Bar */}
                      {progressData.status === "running" && (
                        <Box>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography level="body-sm">
                              {progressData.stats.completed_entities} /{" "}
                              {progressData.stats.total_entities} entities
                            </Typography>
                            <Typography level="body-sm" fontWeight="bold">
                              {progressPercentage}%
                            </Typography>
                          </Stack>
                          <LinearProgress
                            determinate
                            value={progressPercentage}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                      )}

                      {/* Current Activity - shows all parallel entities */}
                      {progressData.current_entities &&
                        progressData.current_entities.length > 0 && (
                          <Alert color="primary" variant="soft">
                            <Stack spacing={0.5}>
                              <Typography level="body-sm" fontWeight="bold">
                                Currently Processing (
                                {progressData.current_entities.length}{" "}
                                parallel):
                              </Typography>
                              {progressData.current_entities.map(
                                (entity, idx) => (
                                  <Typography key={idx} level="body-sm">
                                    • {entity.name} ({entity.index_name})
                                    {entity.field_name && (
                                      <>
                                        {" "}
                                        → <strong>{entity.field_name}</strong>
                                      </>
                                    )}
                                  </Typography>
                                ),
                              )}
                            </Stack>
                          </Alert>
                        )}
                      {/* Fallback for old API response without current_entities */}
                      {(!progressData.current_entities ||
                        progressData.current_entities.length === 0) &&
                        progressData.current_entity && (
                          <Alert color="primary" variant="soft">
                            <Stack>
                              <Typography level="body-sm" fontWeight="bold">
                                Currently Processing:
                              </Typography>
                              <Typography level="body-sm">
                                {progressData.current_entity.name} (
                                {progressData.current_entity.index_name})
                                {progressData.current_entity.field_name && (
                                  <>
                                    {" "}
                                    →{" "}
                                    <strong>
                                      {progressData.current_entity.field_name}
                                    </strong>
                                  </>
                                )}
                              </Typography>
                            </Stack>
                          </Alert>
                        )}

                      <Divider />

                      {/* Stats Row */}
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Chip color="neutral" variant="soft" size="lg">
                          Entities: {progressData.stats.completed_entities}/
                          {progressData.stats.total_entities}
                        </Chip>
                        <Chip color="success" variant="soft" size="lg">
                          Fields: {progressData.stats.completed_fields}
                        </Chip>
                        {progressData.stats.errors > 0 && (
                          <Chip color="danger" variant="soft" size="lg">
                            Errors: {progressData.stats.errors}
                          </Chip>
                        )}
                      </Stack>

                      <Divider />

                      {/* Recent Items Table */}
                      <Box>
                        <Typography level="title-sm" sx={{ mb: 1 }}>
                          Recently Indexed Fields
                        </Typography>
                        {progressData.recent_items.length > 0 ? (
                          <Sheet
                            variant="outlined"
                            sx={{
                              borderRadius: "sm",
                              overflow: "auto",
                              maxHeight: 200,
                            }}
                          >
                            <Table size="sm" stickyHeader>
                              <thead>
                                <tr>
                                  <HeaderCell style={{ width: "30%" }}>
                                    Entity
                                  </HeaderCell>
                                  <HeaderCell style={{ width: "20%" }}>
                                    Field
                                  </HeaderCell>
                                  <HeaderCell style={{ width: "15%" }}>
                                    Status
                                  </HeaderCell>
                                  <HeaderCell style={{ width: "15%" }}>
                                    Type
                                  </HeaderCell>
                                  <HeaderCell style={{ width: "20%" }}>
                                    Time
                                  </HeaderCell>
                                </tr>
                              </thead>
                              <tbody>
                                {progressData.recent_items.map((item, idx) => (
                                  <tr
                                    key={`${item.entity_id}-${item.field_name}-${idx}`}
                                  >
                                    <td>
                                      <Typography level="body-sm" noWrap>
                                        {item.entity_name}
                                      </Typography>
                                    </td>
                                    <td>
                                      <Typography
                                        level="body-xs"
                                        fontWeight="bold"
                                      >
                                        {item.field_name}
                                      </Typography>
                                    </td>
                                    <td>
                                      <StatusChip status={item.status} />
                                    </td>
                                    <td>
                                      <Typography
                                        level="body-xs"
                                        color="neutral"
                                      >
                                        {item.index_name}
                                      </Typography>
                                    </td>
                                    <td>
                                      <Typography
                                        level="body-xs"
                                        color="neutral"
                                      >
                                        {formatDateTime(item.completed_at)}
                                      </Typography>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </Sheet>
                        ) : (
                          <Typography level="body-sm" color="neutral">
                            No recent indexing activity
                          </Typography>
                        )}
                      </Box>

                      {/* Completion Info */}
                      {progressData.status === "completed" &&
                        progressData.completed_at && (
                          <Alert color="success" variant="soft">
                            <Typography level="body-sm">
                              Indexing completed at{" "}
                              {formatDateTime(progressData.completed_at)}
                            </Typography>
                          </Alert>
                        )}

                      {progressData.status === "error" &&
                        progressData.error_message && (
                          <Alert color="danger" variant="soft">
                            <Typography level="body-sm">
                              Error: {progressData.error_message}
                            </Typography>
                          </Alert>
                        )}
                    </>
                  )}
                </Stack>
              </TabPanel>
            </Tabs>

            <Divider />

            {/* Actions */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="solid"
                color="primary"
                startDecorator={
                  triggering ? (
                    <CircularProgress size="sm" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={handleTrigger}
                disabled={triggering}
              >
                {triggering ? "Triggering..." : "Index Now"}
              </Button>
              <Button variant="outlined" color="neutral" onClick={onClose}>
                Close
              </Button>
            </Stack>
          </Stack>
        )}

        {/* CSS for spinning animation */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </ModalDialog>
    </Modal>

    <Modal open={showExplainer} onClose={() => setShowExplainer(false)}>
      <ModalDialog sx={{ maxWidth: 420 }}>
        <BkgIndexingExplainer />
        <Button
          variant="soft"
          color="neutral"
          onClick={() => setShowExplainer(false)}
          sx={{ alignSelf: "flex-end", mt: 1 }}
        >
          Close
        </Button>
      </ModalDialog>
    </Modal>
    </>
  );
};

export default ViewQueueModal;
